import { useMemo, useState } from 'react';
import { useAppStore } from '../store/useAppStore.js';
import { Status, ANNUAL_MULTIPLIER } from '@launchpad/shared';

export function SalaryComparison() {
  const applications = useAppStore((s) => s.applications);
  const offers = useMemo(() => applications.filter((a) => a.status === Status.OFFER && a.salary), [applications]);
  const [selected, setSelected] = useState(new Set());

  function toggle(appId) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(appId) ? next.delete(appId) : next.add(appId);
      return next;
    });
  }

  const compared = offers.filter((a) => selected.has(a.appId));

  function annualize(salary) {
    if (!salary) return { min: 0, max: 0 };
    const mult = ANNUAL_MULTIPLIER[salary.period] || 1;
    return { min: (salary.min || 0) * mult, max: (salary.max || 0) * mult };
  }

  return (
    <div className="p-6 space-y-6 max-w-5xl">
      <h1 className="text-lg font-semibold text-slate-900">Salary Comparison</h1>

      {offers.length === 0 ? (
        <p className="text-sm text-slate-400 italic">No offers to compare yet</p>
      ) : (
        <>
          <div className="space-y-1">
            <p className="text-xs text-slate-500">Select offers to compare:</p>
            {offers.map((app) => (
              <label key={app.appId} className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={selected.has(app.appId)} onChange={() => toggle(app.appId)} className="rounded border-slate-300" />
                {app.company} — {app.role}
              </label>
            ))}
          </div>

          {compared.length > 0 && (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {compared.map((app) => {
                const annual = annualize(app.salary);
                return (
                  <div key={app.appId} className="rounded border border-slate-200 p-4 space-y-2">
                    <p className="font-medium text-slate-900">{app.company}</p>
                    <p className="text-sm text-slate-600">{app.role}</p>
                    <div className="text-sm space-y-1">
                      <p>
                        <span className="text-slate-500">Original:</span>{' '}
                        {app.salary.currency} {app.salary.min?.toLocaleString()}–{app.salary.max?.toLocaleString()} / {app.salary.period}
                      </p>
                      <p className="font-medium text-primary-700">
                        Annual: {app.salary.currency} {annual.min.toLocaleString()}–{annual.max.toLocaleString()}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
